import { List, Map, fromJS } from 'immutable';
import { format, subDays, subHours, subMinutes, subWeeks } from 'date-fns';

import { isIos, isRunningAsPWA } from './browser_utils';
import { isTodoKeywordCompleted, STATIC_FILE_PREFIX } from './org_utils';
import { dateForTimestamp } from './timestamps';

export const NOTIFICATION_TIME_UNITS = ['m', 'h', 'd', 'w'];

export const PLANNING_TYPE_TO_SETTING = {
  SCHEDULED: 'scheduled',
  DEADLINE: 'deadline',
  TIMESTAMP_TITLE: 'timestamp',
  TIMESTAMP_DESCRIPTION: 'timestamp',
};

const TYPE_LABELS = {
  scheduled: 'SCHEDULED',
  deadline: 'DEADLINE',
  timestamp: 'Timestamp',
};

const UNIT_LABELS = {
  m: ['minute', 'minutes'],
  h: ['hour', 'hours'],
  d: ['day', 'days'],
  w: ['week', 'weeks'],
};

export const DEFAULT_LEAD_TIMES = fromJS({
  scheduled: { enabled: false, value: 15, unit: 'm' },
  deadline: { enabled: false, value: 1, unit: 'd' },
  timestamp: { enabled: false, value: 15, unit: 'm' },
});

export const DEFAULT_NOTIFICATION_SETTINGS = DEFAULT_LEAD_TIMES.merge(
  fromJS({
    enabled: false,
    tagCadences: [],
  })
);

export const EMPTY_TAG_CADENCE = {
  tag: '',
  scheduled: { enabled: false, value: 15, unit: 'm' },
  deadline: { enabled: false, value: 1, unit: 'd' },
  timestamp: { enabled: false, value: 15, unit: 'm' },
};

export const areNotificationsSupported = () => {
  if (typeof window === 'undefined' || !window.isSecureContext) {
    return false;
  }
  if (
    !('Notification' in window) ||
    !('serviceWorker' in navigator) ||
    !('PushManager' in window)
  ) {
    return false;
  }
  if (isIos() && !isRunningAsPWA) {
    return false;
  }
  return true;
};

export const getNotificationsDisabledReason = () => {
  if (typeof window === 'undefined' || !window.isSecureContext) {
    return 'Notifications require a secure (HTTPS) connection.';
  }
  if (isIos() && !isRunningAsPWA) {
    return 'On iOS, add organice to your Home Screen to enable notifications.';
  }
  if (
    !('Notification' in window) ||
    !('serviceWorker' in navigator) ||
    !('PushManager' in window)
  ) {
    return 'Notifications are not supported in this browser.';
  }
  return null;
};

export const subtractLeadTime = (date, value, unit) => {
  const amount = Number(value) || 0;
  switch (unit) {
    case 'm':
      return subMinutes(date, amount);
    case 'h':
      return subHours(date, amount);
    case 'd':
      return subDays(date, amount);
    case 'w':
      return subWeeks(date, amount);
    default:
      return date;
  }
};

export const formatLeadTime = (value, unit) => {
  const amount = Number(value) || 0;
  const labels = UNIT_LABELS[unit] || UNIT_LABELS.m;
  return `${amount} ${amount === 1 ? labels[0] : labels[1]}`;
};

export const notificationBody = (settingKey, eventDate, rule) => {
  const label = TYPE_LABELS[settingKey] || settingKey;
  const when = format(eventDate, 'yyyy-MM-dd eee HH:mm');
  const value = Number(rule.get('value')) || 0;
  if (value === 0) {
    return `${label} now: ${when}`;
  }
  return `${label} in ${formatLeadTime(value, rule.get('unit'))}: ${when}`;
};

export const ruleSetsForHeader = (header, notificationSettings) => {
  const tags = header.getIn(['titleLine', 'tags']) || List();
  const tagCadences = notificationSettings.get('tagCadences') || List();
  const matching = tagCadences.filter((cadence) => {
    const tag = (cadence.get('tag') || '').trim();
    return tag && tags.includes(tag);
  });

  if (matching.size > 0) {
    return matching;
  }

  return List([notificationSettings]);
};

const GRACE_MS = 2 * 60 * 1000;
const HORIZON_MS = 7 * 24 * 60 * 60 * 1000;

export const collectUpcomingNotifications = (files, notificationSettings, now = new Date()) => {
  if (!notificationSettings || !notificationSettings.get('enabled')) {
    return [];
  }

  const events = [];

  (files || Map()).forEach((file, path) => {
    if (!path || path.startsWith(STATIC_FILE_PREFIX)) {
      return;
    }

    const headers = file.get('headers') || List();
    const todoKeywordSets = file.get('todoKeywordSets');

    headers.forEach((header) => {
      const todoKeyword = header.getIn(['titleLine', 'todoKeyword']);
      if (todoKeyword && todoKeywordSets && isTodoKeywordCompleted(todoKeywordSets, todoKeyword)) {
        return;
      }

      const ruleSets = ruleSetsForHeader(header, notificationSettings);
      const planningItems = header.get('planningItems') || List();
      const title = (header.getIn(['titleLine', 'rawTitle']) || '').trim() || 'Untitled';

      planningItems.forEach((item) => {
        const settingKey = PLANNING_TYPE_TO_SETTING[item.get('type')];
        if (!settingKey) {
          return;
        }

        const timestamp = item.get('timestamp');
        if (!timestamp || timestamp.get('isActive') === false) {
          return;
        }

        const eventDate = dateForTimestamp(timestamp);

        ruleSets.forEach((ruleSet) => {
          const rule = ruleSet.get(settingKey);
          if (!rule || !rule.get('enabled')) {
            return;
          }

          const fireAt = subtractLeadTime(eventDate, rule.get('value'), rule.get('unit'));
          const delta = fireAt.getTime() - now.getTime();
          if (delta < -GRACE_MS || delta > HORIZON_MS) {
            return;
          }

          events.push({
            id: `${path}:${header.get('id')}:${item.get('id')}:${settingKey}:${fireAt.getTime()}`,
            fireAt,
            eventDate,
            title,
            body: notificationBody(settingKey, eventDate, rule),
            path,
            headerId: header.get('id'),
            settingKey,
          });
        });
      });
    });
  });

  events.sort((a, b) => a.fireAt.getTime() - b.fireAt.getTime());
  return events;
};
